import { createElement, Fragment } from "react";
/**
 * WordPress dependencies
 */
import { Button, Modal, __experimentalHStack as HStack, privateApis as componentsPrivateApis } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useMemo, useState } from '@wordpress/element';
import { moreVertical } from '@wordpress/icons';

/**
 * Internal dependencies
 */
import { unlock } from './lock-unlock';
const {
  DropdownMenuV2: DropdownMenu,
  DropdownMenuGroupV2: DropdownMenuGroup,
  DropdownMenuItemV2: DropdownMenuItem,
  DropdownMenuItemLabelV2: DropdownMenuItemLabel,
  kebabCase
} = unlock(componentsPrivateApis);
function ButtonTrigger({
  action,
  onClick
}) {
  return createElement(Button, {
    label: action.label,
    icon: action.icon,
    isDestructive: action.isDestructive,
    size: "compact",
    onClick: onClick
  });
}
function DropdownMenuItemTrigger({
  action,
  onClick
}) {
  return createElement(DropdownMenuItem, {
    onClick: onClick,
    hideOnClick: !action.RenderModal
  }, createElement(DropdownMenuItemLabel, null, action.label));
}
function ActionWithModal({
  action,
  item,
  ActionTrigger
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const actionTriggerProps = {
    action,
    onClick: () => setIsModalOpen(true)
  };
  const {
    RenderModal,
    hideModalHeader
  } = action;
  return createElement(Fragment, null, createElement(ActionTrigger, {
    ...actionTriggerProps
  }), isModalOpen && createElement(Modal, {
    title: action.modalHeader || action.label,
    __experimentalHideHeader: !!hideModalHeader,
    onRequestClose: () => {
      setIsModalOpen(false);
    },
    overlayClassName: `dataviews-action-modal dataviews-action-modal__${kebabCase(action.id)}`
  }, createElement(RenderModal, {
    items: [item],
    closeModal: () => setIsModalOpen(false)
  })));
}
function ActionsDropdownMenuGroup({
  actions,
  item
}) {
  return createElement(DropdownMenuGroup, null, actions.map(action => {
    if (!!action.RenderModal) {
      return createElement(ActionWithModal, {
        key: action.id,
        action: action,
        item: item,
        ActionTrigger: DropdownMenuItemTrigger
      });
    }
    return createElement(DropdownMenuItemTrigger, {
      key: action.id,
      action: action,
      onClick: () => action.callback([item])
    });
  }));
}
export default function ItemActions({
  item,
  actions,
  isCompact
}) {
  const {
    primaryActions,
    eligibleActions
  } = useMemo(() => {
    // If an action is eligible for all items, doesn't need
    // to provide the `isEligible` function.
    const _eligibleActions = actions.filter(action => !action.isEligible || action.isEligible(item));
    const _primaryActions = _eligibleActions.filter(action => action.isPrimary && !!action.icon);
    return {
      primaryActions: _primaryActions,
      eligibleActions: _eligibleActions
    };
  }, [actions, item]);
  if (isCompact) {
    return createElement(CompactItemActions, {
      item: item,
      actions: eligibleActions
    });
  }
  return createElement(HStack, {
    spacing: 1,
    justify: "flex-end",
    className: "dataviews-item-actions",
    style: {
      flexShrink: '0',
      width: 'auto'
    }
  }, !!primaryActions.length && primaryActions.map(action => {
    if (!!action.RenderModal) {
      return createElement(ActionWithModal, {
        key: action.id,
        action: action,
        item: item,
        ActionTrigger: ButtonTrigger
      });
    }
    return createElement(ButtonTrigger, {
      key: action.id,
      action: action,
      onClick: () => action.callback([item])
    });
  }), createElement(CompactItemActions, {
    item: item,
    actions: eligibleActions
  }));
}
function CompactItemActions({
  item,
  actions
}) {
  return createElement(DropdownMenu, {
    trigger: createElement(Button, {
      size: "compact",
      icon: moreVertical,
      label: __('Actions'),
      disabled: !actions.length,
      className: "dataviews-all-actions-button"
    }),
    placement: "bottom-end"
  }, createElement(ActionsDropdownMenuGroup, {
    actions: actions,
    item: item
  }));
}
//# sourceMappingURL=item-actions.js.map